import type { ReactNode } from "react";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

// Note: pdfUrl must be a blob URL or backend endpoint supplied by the parent
// app. No data fetching occurs here — the URL is passed as a prop.

const DEFAULT_VERDICT =
	"This certificate is the official, verifiable record. Download or share it as proof.";

export interface CertificateViewerProps {
	pdfUrl: string;
	title?: string;
	/** Plain-language verdict shown above the document. Falls back to a default. */
	verdict?: string;
	className?: string;
}

function CertificateViewer({
	pdfUrl,
	title,
	verdict,
	className,
}: CertificateViewerProps): ReactNode {
	const verdictText = verdict ?? DEFAULT_VERDICT;

	return (
		<div
			className={cn(
				"flex flex-col gap-2 rounded-lg border border-border",
				className,
			)}
			aria-label={title ?? "Certificate viewer"}
		>
			<div className="flex flex-col gap-1 border-b border-border px-4 py-3">
				{title && <Heading size="sm">{title}</Heading>}
				<Text size="sm" textColor="mutedForeground">
					{verdictText}
				</Text>
			</div>
			<iframe
				src={pdfUrl}
				title={title ?? "Certificate PDF"}
				className="h-[600px] w-full rounded-b-lg"
				aria-label={title ?? "Certificate PDF viewer"}
			/>
		</div>
	);
}
CertificateViewer.displayName = "CertificateViewer";

export default CertificateViewer;
export { CertificateViewer };
